import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { useAuth } from '@/src/components/AuthContextProvider'
import { Button } from '@/src/components/ui/button'
import { Label } from '@/src/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/src/components/ui/select'
import { OperationTypeUpsertForm } from '@/src/components/manage/forms/OperationTypeUpsertForm'
import { getOperationType } from '@/src/api/operation-type'
import { languages } from '@/i18next.config'

const LOCALES = [...new Set(languages.map((l) => l.split('-')[0]))]

export const Route = createFileRoute('/_manage/manage/admin/operations/types_/$typeId/edit')({
  component: OperationTypeEditPage,
})

function OperationTypeEditPage() {
  const navigate = useNavigate()
  const { typeId } = useParams({ from: '/_manage/manage/admin/operations/types_/$typeId/edit' })
  const { isAdmin } = useAuth()
  const [locale, setLocale] = useState<string>(LOCALES[0] || 'en')

  useEffect(() => {
    if (!isAdmin) {
      navigate({ to: '/not-permitted' })
    }
  }, [isAdmin, navigate])

  const { data: operationType, isLoading } = useQuery({
    queryKey: ['operationType', typeId],
    queryFn: () => getOperationType({ data: { id: typeId } }),
    enabled: !!typeId,
  })

  const handleSuccess = () => {
    navigate({ to: '/manage/admin/operations/types' })
  }

  const handleCancel = () => {
    navigate({ to: '/manage/admin/operations/types' })
  }

  return (
    <div className="flex flex-col h-full">
      <AdminToolbar>
        <AdminToolbar.Right>
          <div className="flex items-center gap-2 h-9">
            <Label className="text-sm leading-none whitespace-nowrap pb-0">Language</Label>
            <Select value={locale} onValueChange={(value) => setLocale(value || LOCALES[0] || 'en')}>
              <SelectTrigger className="w-16 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOCALES.map((entry) => (
                  <SelectItem key={entry} value={entry}>
                    {entry.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </AdminToolbar.Right>
      </AdminToolbar>
      <div className="flex-1 min-h-0">
        <div className="h-full overflow-auto">
          {!isLoading && operationType && (
            <OperationTypeUpsertForm
              operationType={operationType}
                onCancel={handleCancel}
              onSuccess={handleSuccess}
              locale={locale}
              onLocaleChange={setLocale}
            />
          )}
        </div>
      </div>
    </div>
  )
}
